/**
searchmodule attribute:
data-filter-query="<filterList>"

Filter element attribute:
data-filter="<filterDefinition>"


Example:
objectKey.subkey.subsubkey,otherObjectKey&123,456#myKey.title?abc

Grammar:
<query>: (<filter><filterSeperator>)<filter>
<filter>: <filterDefinition><valueList>
<filterDefinition>: <fieldAccesorList><FilterOperator>
<fieldAccesorList>: (<fieldAccesor><fieldAccessorListSeperator>)*<fieldAccesor>
<fieldAccesor>: (<pathChoice><fieldAccessorSeperator>)*<pathChoice>
<pathChoice>: <identifier><pathFilter>?
<pathFilter>: "[" <query> "]"
<valueList>: (<string><valueListSeperator>)*<string>
<filterSeperator>: "#"
<FilterOperator>: <SearchOperator> | "&" | "|"
<SearchOperator>: "?"
<fieldAccessorSeperator>: "."
<fieldAccessorListSeperator>: ","
<valueListSeperator>: ","
<string>: [a-z\_,\-0-9]+ // any json value
<identifier>: [a-z_-]+ // json key values
```
 */

import { either, monoid, option } from "fp-ts";
import { Either } from "fp-ts/lib/Either";
import { NonEmptyArray } from "fp-ts/lib/NonEmptyArray";
import { run } from "parser-ts/lib/code-frame";
import * as C from "parser-ts/lib/char";
import * as S from "parser-ts/lib/string";
import * as P from "parser-ts/lib/Parser";
import { pipe } from "fp-ts/lib/function";
import { only, singleQuotedString, UGParser } from "./parserExtensions";
import { Stream } from "parser-ts/lib/Stream";
import { ParseResult } from "parser-ts/lib/ParseResult";
import { Option } from "fp-ts/lib/Option";

export type FilterType =
  | "and"
  | "or"
  | "search"
  | "not"
  | "before"
  | "after"
  | "expired";

export type Query = NonEmptyArray<Filter>;

export type PathChoice = {
  pathKey: string;
  pathFilter: Option<Query>;
};

export type Accessor = NonEmptyArray<PathChoice>;

export type FilterDefinition = {
  type: FilterType;
  propertyPaths: NonEmptyArray<Accessor>;
};

export type Filter = {
  definition: FilterDefinition;
  values: string[];
};

// -------------------------------------------------------------------------------------
// constructors
// -------------------------------------------------------------------------------------

export const PathChoice = (
  pathKey: string,
  pathFilter: Option<Query> = option.none
): PathChoice => ({ pathKey, pathFilter });
export const FilterDefinition = (
  type: FilterType,
  propertyPaths: NonEmptyArray<Accessor>
): FilterDefinition => ({ type, propertyPaths });
export const Filter = (
  definition: FilterDefinition,
  values: string[]
): Filter => ({ definition, values });

// <FilterOperator>: "?" | "&" | "|"
const filterOperatorSearchParser = pipe(
  C.char("?"),
  P.map<string, FilterType>(() => "search")
);
const filterOperatorAndParser = pipe(
  C.char("&"),
  P.map<string, FilterType>(() => "and")
);
const filterOperatorOrParser = pipe(
  C.char("|"),
  P.map<string, FilterType>(() => "or")
);
const filterOperatorNotParser = pipe(
  C.char("~"),
  P.map<string, FilterType>(() => "not")
);
const filterOperatorBeforeParser = pipe(
  C.char("<"),
  P.map<string, FilterType>(() => "before")
);
const filterOperatorAfterParser = pipe(
  C.char(">"),
  P.map<string, FilterType>(() => "after")
);
const filterOperatorExpiredParser = pipe(
  C.char("!"),
  P.map<string, FilterType>(() => "expired")
);
const filterOperatorParser = pipe(
  filterOperatorSearchParser,
  P.alt(() => filterOperatorAndParser),
  P.alt(() => filterOperatorOrParser),
  P.alt(() => filterOperatorNotParser),
  P.alt(() => filterOperatorBeforeParser),
  P.alt(() => filterOperatorAfterParser),
  P.alt(() => filterOperatorExpiredParser)
);
// <string>: [a-z\_,\-0-9]+ // any json value
// <identifier>: [a-z_-]+ // json key values
const identifier = C.many1(C.alphanum);

// <fieldAccessorSeperator>: "."
const AccessorPartSeperatorParser = C.char(".");
// <fieldAccessorListSeperator>: ","
const AccessorListSeperatorParser = C.char(",");

// <pathFilter>: "[" <query> "]"
const AccessorPathFilterParser = P.between(
  C.char("["),
  C.char("]")
)(queryParser);

// <pathChoice>: <identifier><pathFilter>?
const AccessorPathChoiceParser: P.Parser<string, PathChoice> = pipe(
  P.of<string, object>({}),
  P.bind("pathKey", () => identifier),
  P.bind("pathFilter", () => P.optional(AccessorPathFilterParser)),
  P.map(({ pathKey, pathFilter }) => PathChoice(pathKey, pathFilter))
);

// <fieldAccesor>: (<pathChoice><fieldAccessorSeperator>)*<pathChoice>
const AccesorParser: P.Parser<string, Accessor> = P.sepBy1(
  AccessorPartSeperatorParser,
  AccessorPathChoiceParser
);

// <fieldAccesorList>: (<fieldAccesor><fieldAccessorListSeperator>)<fieldAccesor>
const AccessorListParser = P.sepBy1(AccessorListSeperatorParser, AccesorParser);

// <value>: ...
const valueParser = P.either(C.many1(C.alphanum), () => singleQuotedString);

// <valueListSeperator>: ","
const valueListSeperatorParser = S.string(",");

// <valueList>: (<value><valueListSeperator>)\*<value>
const valueListParser = P.sepBy1(valueListSeperatorParser, valueParser);

// <filterDefinition>: <fieldAccesorList><FilterOperator>
const filterDefinitionParser: P.Parser<string, FilterDefinition> = pipe(
  P.of<string, object>({}),
  P.bind("propertyPaths", () => AccessorListParser),
  P.bind("type", () => filterOperatorParser),
  P.map(({ propertyPaths, type }) => FilterDefinition(type, propertyPaths))
);

// <filterSeperator>: "#"
const filterSeperatorParser = C.char("#");

// <filter>: <filterDefinition><valueList>
const filterParser: P.Parser<string, Filter> = pipe(
  filterDefinitionParser,
  P.chain((definition) =>
    pipe(
      valueListParser,
      P.map((valueList) => ({
        definition: definition,
        values: valueList,
      }))
    )
  )
);

// <query>: (<filter><filterSeperator>)<filter>
// is var instead of const to let it be hoisted
function queryParser(
  i: Stream<string>
): ParseResult<string, NonEmptyArray<Filter>> {
  return P.sepBy1(filterSeperatorParser, filterParser)(i);
}

type FilterParseResult<T> = Either<string, T>;

export class FilterParser extends UGParser {
  static parseFilterList(
    input: string
  ): FilterParseResult<NonEmptyArray<Filter>> {
    return FilterParser.parse(queryParser)(input);
  }

  static parseFilterDefinition(
    input: string
  ): FilterParseResult<FilterDefinition> {
    return FilterParser.parse(filterDefinitionParser)(input);
  }

  static parseFieldAccesor(input: string): FilterParseResult<Accessor> {
    return FilterParser.parse(AccesorParser)(input);
  }

  static parseFieldsAccesor(
    input: string
  ): FilterParseResult<NonEmptyArray<Accessor>> {
    return FilterParser.parse(AccessorListParser)(input);
  }
}
